import React, { useEffect, useRef, useState, } from "react";
import { useLocation } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger, ScrollToPlugin } from "gsap/all";
import AOS from "aos";
import "aos/dist/aos.css";
import {
    FaBuilding,
    FaHome,
    FaQuoteLeft,
    FaStar,
    FaMapMarkedAlt,
    FaChevronLeft,
    FaChevronRight,
    FaSearch,
    FaTag,
    FaChartLine,
    FaFileContract,
    FaClipboardCheck,
    FaUsers,
    FaLaptop,
    FaCertificate,
    FaShieldAlt,
    FaEye,
} from "react-icons/fa";
import { HiEye } from "react-icons/hi";
import { FiArrowRight } from "react-icons/fi";
import { useNavigate, Link } from "react-router-dom";

import Navbar from "./Navbar"
import Footer from "./Footer";
import { submitEnquiry } from "../utils/submitEnquiry";
import {
    canSubmitEnquiryForm,
    sanitizeIndianMobileInput,
} from "../utils/phoneValidation";
import AboutLandmarksRealty from "../assets/homePage/landmarks-realty.png";
import {
    handleExclusiveVideoPause,
    handleExclusiveVideoPlay,
} from "../utils/exclusiveVideoPlayback";
import { allProperties } from "../data/propertiesData";
import MdHussainPhoto from "../assets/feedbackImage/mdhussain.jpeg";
import JugnuKumariPhoto from "../assets/feedbackImage/jugnukumari.jpeg";
import SangeetaGoyalPhoto from "../assets/feedbackImage/sangeetagoyal.jpeg";


gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

export default function LandingPage() {
    const navigate = useNavigate();

    const [likedProperties, setLikedProperties] = useState({});
    const heroAutoScrollPausedRef = useRef(false);
    const heroRef = useRef(null);
    const [contactOpen, setContactOpen] = useState(false);
    /** Stops the 2s auto-popup from reopening after the user closes or submits */
    const suppressContactAutoOpenRef = useRef(false);
    const [callbackForm, setCallbackForm] = useState({
        name: "",
        email: "",
        phone: "",
        message: "",
    });
    const [enquirySubmitting, setEnquirySubmitting] = useState(false);

    const [interestOpen, setInterestOpen] = useState(false);
    const [interest, setInterest] = useState("I'm interested in");

    const location = useLocation();

    const [currentSlide, setCurrentSlide] = useState(0);
    const [heroPropertyIndex, setHeroPropertyIndex] = useState(0);
    const [heroMediaIndex, setHeroMediaIndex] = useState(0);

    const heroProperty = allProperties[heroPropertyIndex];
    const heroMedia = heroProperty.media?.length
        ? heroProperty.media
        : [{ type: "image", src: heroProperty.image, label: heroProperty.title }];
    const activeHeroMedia = heroMedia[Math.min(heroMediaIndex, heroMedia.length - 1)];
    const heroPriceParts = heroProperty.priceDisplay.split("/");
    const heroLiked = Boolean(likedProperties[heroProperty.id]);

    useEffect(() => {
        setHeroMediaIndex(0);
    }, [heroPropertyIndex]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (heroAutoScrollPausedRef.current) return;
            setHeroPropertyIndex((prev) => (prev + 1) % allProperties.length);
        }, 5000);

        return () => clearInterval(interval);
    }, []);

    // Add this useEffect to handle hash navigation from other pages
    useEffect(() => {
        if (location.hash) {
            setTimeout(() => {
                const element = document.querySelector(location.hash);
                if (element) {
                    const offset = 90;
                    const top = element.getBoundingClientRect().top + window.pageYOffset - offset;
                    window.scrollTo({ top, behavior: 'smooth' });
                }
            }, 100);
        }
    }, [location]);

    const smoothScrollTo = (id, offset = 80) => {
        const target = document.querySelector(id);
        if (!target) return;

        const targetPosition =
            target.getBoundingClientRect().top +
            window.pageYOffset -
            offset; // 👈 STOP BEFORE

        const startPosition = window.pageYOffset;
        const distance = targetPosition - startPosition;
        const duration = 900;
        let start = null;

        const easeInOutCubic = (t) =>
            t < 0.5
                ? 4 * t * t * t
                : 1 - Math.pow(-2 * t + 2, 3) / 2;

        const animation = (currentTime) => {
            if (!start) start = currentTime;
            const timeElapsed = currentTime - start;
            const progress = Math.min(timeElapsed / duration, 1);

            window.scrollTo(
                0,
                startPosition + distance * easeInOutCubic(progress)
            );

            if (timeElapsed < duration) {
                requestAnimationFrame(animation);
            }
        };

        requestAnimationFrame(animation);
    };


    useEffect(() => {
        AOS.init({ duration: 800, once: true });

        // Contact modal auto-open after 2 seconds (only if not dismissed/submitted)
        const t = setTimeout(() => {
            if (!suppressContactAutoOpenRef.current) {
                setContactOpen(true);
            }
        }, 2000);
        return () => clearTimeout(t);
    }, []);

    useEffect(() => {
        const el = heroRef.current;
        if (!el) return;

        // Parallax hero image and subtle floating animations
        gsap.to(el.querySelectorAll(".hero-float"), {
            yPercent: -8,
            ease: "sine.inOut",
            repeat: -1,
            yoyo: true,
            stagger: 0.35,
            duration: 4,
        });

        // Hero background parallax on scroll
        gsap.to(el, {
            backgroundPosition: "50% 30%",
            ease: "none",
            scrollTrigger: {
                trigger: el,
                scrub: 0.6,
            },
        });

        // Stagger-in for featured property cards
        gsap.from(".property-card", {
            opacity: 0,
            y: 30,
            duration: 0.8,
            stagger: 0.12,
            scrollTrigger: {
                trigger: ".featured-section",
                start: "top 80%",
            },
        });

        // Small navbar color change on scroll
        const nav = document.getElementById("main-nav");
        ScrollTrigger.create({
            start: "top -80",
            onEnter: () =>
                nav.classList.add(
                    "backdrop-blur-md",
                    "bg-white/60",
                    "dark:bg-slate-900/80",
                    "shadow-sm"
                ),
            onLeaveBack: () =>
                nav.classList.remove(
                    "backdrop-blur-md",
                    "bg-white/60",
                    "dark:bg-slate-900/80",
                    "shadow-sm"
                ),
        });
    }, []);

    const testimonials = [
        {
            id: 1,
            name: "MD KHAIRUL HUSSAIN",
            loc: "Delhi",
            tagline: "Stress‑free from day one",
            image: MdHussainPhoto,
            text: "The team really listened to what I wanted and never pushed anything that didn't fit my budget. Every step was explained clearly, and closing on my apartment felt surprisingly easy.",
        },
        {
            id: 2,
            name: "Jugnu Kumari",
            loc: "Noida",
            tagline: "Great support for first‑time buyers",
            image: JugnuKumariPhoto,
            text: "As a first‑time buyer, I had a ton of questions, but they were patient and transparent throughout. They helped me compare projects, handle the paperwork, and lock in a deal I felt confident about.",
        },
        {
            id: 3,
            name: "Sangeeta Goyal",
            loc: "Noida",
            tagline: "Professional and reliable service",
            image: SangeetaGoyalPhoto,
            text: "Updates were always on time, site visits were well organized, and negotiations were handled smartly. I felt I had a solid partner on my side, not just a broker chasing a commission.",
        },
    ];

    const callbackFormValid = canSubmitEnquiryForm(callbackForm);

    async function handleContactSubmit(e) {
        e.preventDefault();
        if (!canSubmitEnquiryForm(callbackForm)) return;

        const phone = sanitizeIndianMobileInput(callbackForm.phone);
        const payload = {
            name: callbackForm.name.trim(),
            email: callbackForm.email.trim(),
            phone,
            property:
                interest && interest !== "I'm interested in"
                    ? interest
                    : "General enquiry",
            message: (callbackForm.message || "").trim(),
            source: "LandingPage Enquire Now",
            submittedAt: new Date().toISOString(),
        };

        setEnquirySubmitting(true);
        try {
            await submitEnquiry(payload);
            suppressContactAutoOpenRef.current = true;
            setContactOpen(false);
            setCallbackForm({ name: "", email: "", phone: "", message: "" });
            setInterest("I'm interested in");
        } catch (error) {
            console.error("Enquiry submission failed:", error);
            alert(error.message || "Sorry, we could not submit your enquiry right now. Please try again.");
        } finally {
            setEnquirySubmitting(false);
        }
    }

    useEffect(() => {
        const links = document.querySelectorAll('a[href^="#"]');

        links.forEach(link => {
            link.addEventListener("click", function (e) {
                e.preventDefault();
                const target = document.querySelector(this.getAttribute("href"));
                if (!target) return;

                const offset = 90;
                const top = target.getBoundingClientRect().top + window.pageYOffset - offset;

                gsap.to(window, {
                    duration: 3.5,   // 🔥 Ultra-smooth slow scroll (increase to 3 or 4 for SUPER slow)
                    scrollTo: top,
                    ease: "power4.out", // 🔥 buttery smooth easing
                });
            });
        });
    }, []);

    // Auto-play functionality
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % testimonials.length);
        }, 4000); // Change slide every 4 seconds

        return () => clearInterval(interval); // Cleanup on unmount
    }, [testimonials.length]);

    const nextSlide = () => {
        setCurrentSlide((prev) => (prev + 1) % testimonials.length);
    };

    const prevSlide = () => {
        setCurrentSlide((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    };

    const services = [
        {
            id: 1,
            icon: <FaSearch />,
            title: "Property Buying Advisory",
            description: "Purchases are guided by foresight, not listings. This service centers on aligning intent with locations and assets that remain relevant over time."
        },
        {
            id: 2,
            icon: <FaTag />,
            title: "Property Selling",
            description: "Value is shaped before it is presented. We refine positioning so your property enters the market with purpose and exits with justification, not compromise."
        },
        {
            id: 3,
            icon: <FaChartLine />,
            title: "Investment Advisory",
            description: "Capital moves best with discipline. This advisory focuses on identifying assets that mature steadily, protect downside, and reward patience over speculation."
        },
        {
            id: 4,
            icon: <FaBuilding />,
            title: "Commercial Assets",
            description: "Commercial choices carry consequences. We assess operational fit, demand continuity, and resilience before recommending any holding."
        },
        {
            id: 5,
            icon: <FaFileContract />,
            title: "Legal & Documentation",
            description: "Clarity lives in documentation. This service exists to eliminate ambiguity, align records, and secure ownership with precision rather than assumption."
        },
        {
            id: 6,
            icon: <FaClipboardCheck />,
            title: "Site Surveys & Due Diligence",
            description: "Reality is verified on ground, not paper. We validate dimensions, boundaries, and site conditions so decisions are made on facts, not projections."
        }
    ];

    const whyChooseFeatures = [
        {
            id: 1,
            icon: <FaLaptop />,
            title: "Smart Tech at Work",
            description: "Virtual meetings, digital paperwork, and fast status updates let you cover more ground with less effort. On‑time data on prices and neighborhood trends helps you pull the trigger at the right moment."
        },
        {
            id: 2,
            icon: <FaShieldAlt />,
            title: "RERA‑Approved",
            description: "Landmarks Realty focuses on RERA projects, giving you clear visibility on approvals, delivery timelines, and commitments. This framework increases buyer safety and keeps transactions transparent and accountable."
        },
        {
            id: 3,
            icon: <FaCertificate />,
            title: "ISO‑Certified",
            description: "ISO‑based systems keep our service, documentation, and follow‑ups organized and consistent. You gain from reliable quality, professional handling, and a team dedicated to high standards every single day."
        }
    ];

    return (
        <div className="min-h-screen font-sans text-slate-800 dark:text-slate-100 bg-white dark:bg-slate-950 overflow-x-hidden">
            {/* NAVBAR */}
            <Navbar />

            {/* HERO */}
            <header
                id="home"
                ref={heroRef}
                className="relative min-h-screen bg-cover bg-center flex items-center lg:items-start overflow-hidden pt-20 sm:pt-24 lg:pt-28"
                style={{
                    backgroundImage: "linear-gradient(135deg, rgba(59,43,154,0.50), rgba(216,59,41,0.20)), url('/assets/hero.jpg')",
                }}
            >
                {/* Animated Background Overlay */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/20 pointer-events-none"></div>

                {/* Floating Particles */}
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    {[...Array(20)].map((_, i) => (
                        <div
                            key={i}
                            className="absolute w-1 h-1 sm:w-2 sm:h-2 bg-white/30 rounded-full animate-float"
                            style={{
                                left: `${Math.random() * 100}%`,
                                top: `${Math.random() * 100}%`,
                                animationDelay: `${Math.random() * 3}s`,
                                animationDuration: `${3 + Math.random() * 4}s`
                            }}
                        ></div>
                    ))}
                </div>

                {/* Gradient Orbs */}
                <div className="absolute top-10 sm:top-20 -left-10 sm:-left-20 w-48 h-48 sm:w-96 sm:h-96 bg-gradient-to-br from-[#3B2B9A]/20 to-purple-500/20 rounded-full blur-3xl animate-pulse pointer-events-none"></div>
                <div className="absolute bottom-10 sm:bottom-20 -right-10 sm:-right-20 w-48 h-48 sm:w-96 sm:h-96 bg-gradient-to-br from-[#D83B29]/20 to-orange-500/20 rounded-full blur-3xl animate-pulse pointer-events-none" style={{ animationDelay: '1s' }}></div>

                <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 flex flex-col lg:flex-row items-center gap-6 sm:gap-8 lg:gap-12 py-6 sm:py-8 lg:py-12 min-h-[calc(100vh-5rem)] lg:min-h-0 pointer-events-auto">
                    {/* Left Content */}
                    <div className="w-full lg:w-6/12 text-white space-y-4 sm:space-y-5 text-center lg:text-left">
                        {/* Main Heading */}
                        <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black leading-[1.1] drop-shadow-2xl animate-slide-up px-2 sm:px-0">
                            Your Trusted Property Consultants
                            <br className="hidden sm:block" />
                            <span className="sm:hidden"> </span>
                            in{" "}
                            <span className="relative inline-block">
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#8B1E1E] via-[#D83B29] to-[#FF4500] animate-gradient">
                                    Noida & Delhi NCR
                                </span>
                                <svg
                                    className="absolute -bottom-1 sm:-bottom-2 left-0 w-full"
                                    height="8"
                                    viewBox="0 0 300 12"
                                    fill="none"
                                >
                                    <path
                                        d="M2 10C50 5 100 2 150 3C200 4 250 7 298 10"
                                        stroke="url(#gradient)"
                                        strokeWidth="3"
                                        strokeLinecap="round"
                                        className="animate-draw-line"
                                    />
                                    <defs>
                                        <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                                            <stop offset="0%" stopColor="#8B1E1E" />
                                            <stop offset="50%" stopColor="#D83B29" />
                                            <stop offset="100%" stopColor="#FF4500" />
                                        </linearGradient>
                                    </defs>
                                </svg>
                            </span>
                        </h1>

                        {/* Description */}
                        <p
                            className="text-sm sm:text-base md:text-lg max-w-xl mx-auto lg:mx-0 opacity-95 leading-relaxed animate-slide-up px-4 sm:px-0"
                            style={{ animationDelay: '0.2s' }}
                        >
                            From buying and selling properties to planning your next move, we help you move ahead with confidence.
                        </p>

                        {/* CTA Buttons */}
                        <div className="flex flex-col sm:flex-row flex-wrap gap-3 sm:gap-4 animate-slide-up justify-center lg:justify-start px-4 sm:px-0" style={{ animationDelay: '0.4s' }}>
                            <Link
                                to="/properties"
                                className="group relative overflow-hidden inline-flex items-center justify-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 rounded-full text-white font-bold shadow-2xl bg-gradient-to-r from-[#3B2B9A] to-[#5A47D8] transition-all duration-300 hover:shadow-[#5A47D8]/50 hover:-translate-y-1 hover:scale-105 text-sm sm:text-base"
                            >
                                <span className="relative z-10 flex items-center gap-2">
                                    Explore Properties
                                    <svg className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                    </svg>
                                </span>
                                <span className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#5A47D8] to-[#7B5FE8] translate-y-full group-hover:translate-y-0 transition-transform duration-300"></span>
                                <span className="pointer-events-none absolute inset-0 bg-white/20 translate-x-[-120%] group-hover:translate-x-[120%] transition-transform duration-700 skew-x-12"></span>
                            </Link>

                            <button
                                onClick={() => setContactOpen(true)}
                                className="group relative overflow-hidden inline-flex items-center justify-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 rounded-full font-bold border-2 border-white/80 text-white bg-white/10 backdrop-blur-xl transition-all duration-300 hover:bg-white hover:text-[#3B2B9A] hover:-translate-y-1 hover:scale-105 hover:shadow-2xl text-sm sm:text-base"
                            >
                                <span className="relative z-10 flex items-center gap-2">
                                    <span className="hidden sm:inline">Schedule a Consultation</span>
                                    <span className="sm:hidden">Get Consultation</span>
                                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="currentColor" viewBox="0 0 20 20">
                                        <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                                    </svg>
                                </span>
                            </button>
                        </div>

                        {/* Feature Tags */}
                        <div className="flex gap-2 sm:gap-3 flex-wrap animate-slide-up justify-center lg:justify-start px-4 sm:px-0" style={{ animationDelay: '0.6s' }}>
                            {[
                                { icon: '✓', text: 'Trusted Agents' },
                                { icon: '✓', text: 'Verified Listings' },
                                { icon: '✓', text: 'Systematic Process' }
                            ].map((item, i) => (
                                <button
                                    key={i}
                                    type="button"
                                    onClick={() => navigate("/properties")}
                                    className="group bg-white/10 backdrop-blur-xl border border-white/20 text-white px-3 sm:px-5 py-2 sm:py-2.5 rounded-full text-xs sm:text-sm font-medium hover:bg-white/20 hover:scale-105 transition-all duration-300 cursor-pointer flex items-center gap-1.5 sm:gap-2"
                                >
                                    <span className="text-amber-300 font-bold group-hover:scale-125 transition-transform text-sm sm:text-base">{item.icon}</span>
                                    {item.text}
                                </button>
                            ))}
                        </div>

                        {/* Stats Bar */}
                        <div className="grid grid-cols-3 gap-2 sm:gap-4 pt-3 sm:pt-4 animate-slide-up max-w-md mx-auto lg:mx-0" style={{ animationDelay: '0.8s' }}>
                            {[
                                { value: '1200+', label: 'Happy Clients' },
                                { value: '90+', label: 'Society Sold' },
                                { value: '9+', label: 'Years Experience' }
                            ].map((stat, i) => (
                                <button
                                    key={i}
                                    type="button"
                                    onClick={() => smoothScrollTo("#properties", 90)}
                                    className="text-center group cursor-pointer w-full rounded-lg py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
                                >
                                    <div className="text-xl sm:text-2xl md:text-3xl font-black text-white group-hover:scale-110 transition-transform">{stat.value}</div>
                                    <div className="text-[10px] sm:text-xs md:text-sm text-white/70 font-medium">{stat.label}</div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Right Property Card — auto-rotates through all properties every 5s */}
                    <div
                        className="w-full lg:w-6/12 relative animate-slide-up px-4 sm:px-0 max-w-lg lg:max-w-none mx-auto"
                        style={{ animationDelay: '0.3s' }}
                        onMouseEnter={() => { heroAutoScrollPausedRef.current = true; }}
                        onMouseLeave={() => { heroAutoScrollPausedRef.current = false; }}
                        onFocusCapture={() => { heroAutoScrollPausedRef.current = true; }}
                        onBlurCapture={() => { heroAutoScrollPausedRef.current = false; }}
                    >
                        <div
                            key={heroProperty.id}
                            className="group relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-2xl border-2 border-white/20 bg-white/5 backdrop-blur-xl hover:scale-[1.02] transition-all duration-500 hover:shadow-[#3B2B9A]/30 hover:border-white/40 animate-hero-property-in"
                        >
                            {/* Image Container — aspect box + cover so photos are never stretched */}
                            <div className={`relative w-full overflow-hidden aspect-[5/3] sm:aspect-[16/9] ${heroProperty.heroImageFit?.objectFit === "contain" ? "bg-white dark:bg-slate-900" : "bg-slate-100 dark:bg-slate-800"}`}>
                                {activeHeroMedia.type === "video" ? (
                                    <video
                                        key={`${heroProperty.id}-video-${heroMediaIndex}`}
                                        className="absolute inset-0 z-10 h-full w-full object-contain"
                                        muted
                                        controls
                                        playsInline
                                        preload="auto"
                                        onPlay={handleExclusiveVideoPlay}
                                        onPause={handleExclusiveVideoPause}
                                        title="Video plays muted until you adjust volume in the controls"
                                    >
                                        <source src={activeHeroMedia.src} type="video/mp4" />
                                        Your browser does not support the video tag.
                                    </video>
                                ) : (
                                    <img
                                        src={activeHeroMedia.src}
                                        alt={`${heroProperty.title} - ${activeHeroMedia.label}`}
                                        style={{
                                            objectFit: heroProperty.heroImageFit?.objectFit ?? "cover",
                                            objectPosition:
                                                heroProperty.heroImageFit?.objectPosition ?? "center center",
                                            ...(heroProperty.heroImageFit?.objectFit !== "contain" && {
                                                transform: `scale(${heroProperty.heroImageFit?.scale ?? 1.03})`,
                                            }),
                                        }}
                                        className={`pointer-events-none absolute inset-0 h-full w-full transition-transform duration-700 ${heroProperty.heroImageFit?.objectFit === "contain" ? "object-contain p-1" : "min-h-full min-w-full object-cover group-hover:scale-105"}`}
                                    />
                                )}
                                {activeHeroMedia.type !== "video" && (
                                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                )}

                                {/* Featured Badge */}
                                <div className="pointer-events-auto absolute top-3 sm:top-4 left-3 sm:left-4 z-20 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-gradient-to-r from-[#D83B29] to-[#FF4500] text-white font-bold text-xs sm:text-sm shadow-lg flex items-center gap-1.5 sm:gap-2 animate-pulse">
                                    <span>⭐</span> 4.9/5 Rating
                                </div>

                                {/* Wishlist Button */}
                                <button
                                    type="button"
                                    onClick={() =>
                                        setLikedProperties((prev) => ({
                                            ...prev,
                                            [heroProperty.id]: !prev[heroProperty.id],
                                        }))
                                    }
                                    className={`pointer-events-auto absolute top-3 sm:top-4 right-3 sm:right-4 z-20 w-10 h-10 sm:w-12 sm:h-12 rounded-full backdrop-blur-xl flex items-center justify-center shadow-lg transition-all duration-300 group ${heroLiked ? "bg-red-50 scale-110" : "bg-white/90 hover:bg-white dark:bg-slate-900 hover:scale-110"}`}
                                >
                                    <svg
                                        className={`w-5 h-5 sm:w-6 sm:h-6 transition-all duration-300 ${heroLiked ? "text-red-500 scale-125" : "text-slate-700 dark:text-slate-200 group-hover:text-red-500 group-hover:scale-125"}`}
                                        viewBox="0 0 24 24"
                                        fill={heroLiked ? "currentColor" : "none"}
                                        stroke="currentColor"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                    </svg>
                                </button>
                            </div>

                            {/* Property Details */}
                            <div className="p-4 sm:p-6 bg-white/95 dark:bg-slate-900 backdrop-blur-xl border-t border-white/20 dark:border-slate-700 text-slate-900 dark:text-slate-50">
                                <div className="flex items-start justify-between mb-3 gap-2">
                                    <div className="flex-1 min-w-0">
                                        <h3 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white group-hover:text-[#3B2B9A] dark:group-hover:text-indigo-300 transition-colors truncate">
                                            {heroProperty.title}
                                        </h3>
                                        <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-300 mt-1 flex items-center gap-1.5 sm:gap-2">
                                            <svg className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0 text-slate-500 dark:text-slate-400" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                                            </svg>
                                            {heroProperty.location}
                                        </p>
                                        <p className="text-[10px] sm:text-xs text-slate-500 dark:text-slate-400 mt-0.5 sm:mt-1 ml-4 sm:ml-6 truncate">
                                            {heroProperty.subLocation}
                                        </p>
                                    </div>
                                    <div className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg sm:rounded-xl bg-gradient-to-r from-[#3B2B9A] to-[#5A47D8] text-white font-bold shadow-lg text-right flex-shrink-0">
                                        <div className="text-xs sm:text-sm whitespace-nowrap">{heroPriceParts[0]?.trim()}</div>
                                        <div className="text-[10px] sm:text-xs opacity-90">/ sq yd</div>
                                    </div>
                                </div>

                                {/* Property Features */}
                                <div className="flex items-center gap-2 sm:gap-4 pt-3 sm:pt-4 border-t border-slate-200 dark:border-slate-600 overflow-x-auto">
                                    {[
                                        { icon: '📐', text: heroProperty.type },
                                        { icon: '🏗️', text: heroProperty.tag },
                                        { icon: '📏', text: 'Various Sizes' },
                                    ].map((feature, i) => (
                                        <div key={i} className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-slate-700 dark:text-slate-200 font-medium whitespace-nowrap">
                                            <span className="text-base sm:text-lg" aria-hidden>{feature.icon}</span>
                                            {feature.text}
                                        </div>
                                    ))}
                                </div>

                                <div className="relative z-20 mt-3 overflow-x-auto media-scrollbar pb-2">
                                    <div className="flex gap-2 min-w-max">
                                        {heroMedia.map((media, index) => (
                                            <button
                                                key={`${heroProperty.id}-${media.type}-${index}`}
                                                type="button"
                                                onClick={() => setHeroMediaIndex(index)}
                                                className={`relative z-10 w-20 h-14 rounded-lg overflow-hidden border-2 transition-all ${heroMediaIndex === index
                                                        ? "border-[#3B2B9A] shadow-md"
                                                        : "border-slate-200 dark:border-slate-600 hover:border-[#5A47D8]"
                                                    }`}
                                            >
                                                {media.type === "video" ? (
                                                    <div className="w-full h-full bg-slate-900 text-white flex items-center justify-center text-xs font-semibold">
                                                        Video
                                                    </div>
                                                ) : (
                                                    <img
                                                        src={media.src}
                                                        alt={media.label}
                                                        className="w-full h-full object-cover"
                                                    />
                                                )}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Property carousel dots */}
                                <div className="mt-3 flex items-center justify-center gap-2">
                                    {allProperties.map((property, index) => (
                                        <button
                                            key={property.id}
                                            type="button"
                                            aria-label={`Show ${property.title}`}
                                            onClick={() => setHeroPropertyIndex(index)}
                                            className={`h-2 rounded-full transition-all duration-300 ${heroPropertyIndex === index
                                                    ? "w-6 bg-[#3B2B9A]"
                                                    : "w-2 bg-slate-300 dark:bg-slate-600 hover:bg-[#5A47D8]"
                                                }`}
                                        />
                                    ))}
                                </div>

                                {/* View Details Button */}
                                <button
                                    type="button"
                                    onClick={() => navigate("/properties")}
                                    className="mt-3 sm:mt-4 w-full py-2.5 sm:py-3 rounded-lg sm:rounded-xl bg-slate-100 dark:bg-slate-800 text-[#3B2B9A] dark:text-indigo-300 font-bold hover:bg-[#3B2B9A] hover:text-white dark:hover:text-white transition-all duration-300 flex items-center justify-center gap-2 group/btn text-sm sm:text-base"
                                >
                                    View Details
                                    <svg className="w-4 h-4 sm:w-5 sm:h-5 group-hover/btn:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Scroll Indicator */}
                <div
                    onClick={() => smoothScrollTo("#about", 100)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            smoothScrollTo("#about", 100);
                        }
                    }}
                    role="button"
                    tabIndex={0}
                    className="absolute bottom-4 sm:bottom-5 left-1/2 -translate-x-1/2 animate-bounce z-30 cursor-pointer pointer-events-auto focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 rounded-full"
                >
                    <div className="flex flex-col items-center gap-2 group">
                        <div className="w-5 h-8 sm:w-6 sm:h-10 rounded-full border-2 border-white/50 flex items-start justify-center p-1.5 sm:p-2 group-hover:border-white transition-colors">
                            <div className="w-1 h-2 sm:w-1.5 sm:h-3 bg-white/70 rounded-full animate-scroll"></div>
                        </div>
                    </div>
                </div>

                <style>{`
                    @keyframes float {
                        0%, 100% { transform: translateY(0px); }
                        50% { transform: translateY(-20px); }
                    }
                    @keyframes gradient {
                        0%, 100% { background-position: 0% 50%; }
                        50% { background-position: 100% 50%; }
                    }
                    @keyframes draw-line {
                        0% { stroke-dasharray: 0 300; }
                        100% { stroke-dasharray: 300 300; }
                    }
                    @keyframes fade-in {
                        from { opacity: 0; transform: translateY(-20px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    @keyframes slide-up {
                        from { opacity: 0; transform: translateY(30px); }
                        to { opacity: 1; transform: translateY(0); }
                    }
                    @keyframes hero-property-in {
                        from { opacity: 0; transform: translateX(24px); }
                        to { opacity: 1; transform: translateX(0); }
                    }
                    @keyframes scroll {
                        0% { transform: translateY(0); opacity: 1; }
                        100% { transform: translateY(12px); opacity: 0; }
                    }
                    .animate-float {
                        animation: float 3s ease-in-out infinite;
                    }
                    .animate-gradient {
                        background-size: 200% auto;
                        animation: gradient 3s ease infinite;
                    }
                    .animate-draw-line {
                        stroke-dasharray: 0 300;
                        animation: draw-line 2s ease-out forwards;
                    }
                    .animate-fade-in {
                        animation: fade-in 0.8s ease-out forwards;
                    }
                    .animate-slide-up {
                        animation: slide-up 0.8s ease-out forwards;
                    }
                    .animate-hero-property-in {
                        animation: hero-property-in 0.55s ease-out forwards;
                    }
                    .animate-scroll {
                        animation: scroll 2s ease-in-out infinite;
                    }
                    .media-scrollbar::-webkit-scrollbar {
                        height: 6px;
                    }
                    .media-scrollbar::-webkit-scrollbar-thumb {
                        background: #a5b4fc;
                        border-radius: 999px;
                    }
                    .media-scrollbar::-webkit-scrollbar-track {
                        background: #e2e8f0;
                        border-radius: 999px;
                    }
                `}</style>
            </header>

            {/* ABOUT */}
            <section id="about" className="mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
                <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-8 sm:gap-10 lg:gap-12 items-center">

                    {/* LEFT IMAGE */}
                    <div
                        data-aos="fade-right"
                        data-aos-duration="900"
                        className="relative order-2 md:order-1"
                    >
                        <div className="rounded-2xl sm:rounded-3xl overflow-hidden shadow-xl sm:shadow-2xl border border-white/20 bg-white/10 backdrop-blur-md p-1">
                            <div className="rounded-xl sm:rounded-2xl overflow-hidden w-full aspect-[4/3]">
                                <img
                                    src={AboutLandmarksRealty}
                                    alt="About Landmarks Realty"
                                    className="h-full w-full object-cover object-center"
                                />
                            </div>
                        </div>

                        {/* Floating Badge */}
                        <div className="absolute -bottom-4 sm:bottom-0 left-0 right-0 sm:right-auto bg-gradient-to-r from-[#3B2B9A] to-[#5A47D8] text-white px-4 sm:px-6 py-2.5 sm:py-3 rounded-xl sm:rounded-2xl shadow-lg mx-4 sm:mx-0 sm:max-w-[85%]">
                            <span className="font-semibold text-xs sm:text-sm lg:text-base text-center sm:text-left block">
                                Over 9+ Years Helping You Find the Perfect Space
                            </span>
                        </div>
                    </div>

                    {/* RIGHT CONTENT */}
                    <div data-aos="fade-left" data-aos-duration="900" className="order-1 md:order-2">
                        {/* Heading */}
                        <h3
                            className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#3B2B9A] dark:text-indigo-300 relative inline-block"
                            data-aos="fade-up"
                        >
                            About Landmarks Realty
                            <span className="absolute left-1/2 -bottom-2 -translate-x-1/2 w-12 sm:w-16 h-0.5 sm:h-1 bg-gradient-to-r from-[#3B2B9A] to-[#5A47D8] rounded-full"></span>
                        </h3>

                        <p className="mt-6 sm:mt-8 text-slate-700 dark:text-slate-200 leading-relaxed text-sm sm:text-base lg:text-lg">
                            Navigating property buying or investing in Noida, UP can be challenging.
                            Unexpected fees, legal hurdles, price fluctuations, and market uncertainty
                            often leave buyers feeling unsure. This is where a trusted property
                            consultant makes all the difference.
                        </p>

                        <p className="mt-4 text-slate-700 dark:text-slate-200 leading-relaxed text-sm sm:text-base lg:text-lg">
                            Landmarks Realty transforms confusion into confidence. Since our inception on <strong>January 19, 2017</strong>, we have prioritized a client-first approach, offering guidance grounded in thorough research, market expertise, and smooth documentation. Whether it’s a residential property or a commercial investment, our experienced team checks that every decision you make is secure and geared toward your long-term success.
                        </p>

                        <div className="mt-6 sm:mt-8 flex items-start justify-start">
                            {/* CTA Button */}
                            <a
                                href="/about"
                                className="relative overflow-hidden inline-flex items-center gap-2 group px-5 sm:px-6 py-2.5 sm:py-3 rounded-full border-2 border-[#3B2B9A] dark:border-indigo-400 text-[#3B2B9A] dark:text-indigo-300 font-semibold transition-all duration-300 hover:scale-105 text-sm sm:text-base"
                            >
                                {/* Background animation layer */}
                                <span className="absolute inset-0 bg-gradient-to-r from-[#3B2B9A] to-[#5A47D8] -translate-x-full group-hover:translate-x-0 transition-transform duration-500 ease-out"></span>

                                {/* Text + Icon (stays on top) */}
                                <span className="relative z-10 flex items-center gap-2">
                                    <span className="group-hover:text-white transition-all duration-300">
                                        Know More
                                    </span>

                                    <FiArrowRight className="text-base sm:text-lg transition-all duration-300 group-hover:translate-x-1 group-hover:text-white" />
                                </span>
                            </a>
                        </div>
                    </div>

                </div>
            </section>

            {/* FEATURED PROPERTIES */}
            <section id="properties" className="featured-section py-16 bg-slate-100 dark:bg-slate-800">
                <div className="mx-auto px-6 lg:px-10">

                    {/* Header */}
                    {/* Header */}
                    <div
                        className="
    flex flex-col gap-6
    sm:flex-row sm:items-center sm:justify-between
    mb-10
  "
                    >
                        {/* Heading */}
                        <h3
                            className="
      text-3xl sm:text-4xl lg:text-4xl
      font-bold text-[#3B2B9A] dark:text-indigo-300
      relative inline-block
      text-center sm:text-left
    "
                            data-aos="fade-up"
                        >
                            Featured Listings
                            <span
                                className="
        absolute left-1/2 sm:left-0
        -bottom-2
        -translate-x-1/2 sm:translate-x-0
        w-12 sm:w-16
        h-1
        bg-gradient-to-r from-[#3B2B9A] to-[#5A47D8]
        rounded-full
      "
                            ></span>
                        </h3>

                        {/* Button */}
                        <button
                            onClick={() => navigate("/properties")}
                            className="
      relative overflow-hidden
      flex items-center justify-center gap-2
      px-4 sm:px-5 py-2
      rounded-full
      border border-[#D83B29]
      text-[#D83B29] font-medium
      transition-all duration-300
      group
      self-center sm:self-auto
    "
                        >
                            {/* Animated Gradient Background */}
                            <span
                                className="
        absolute inset-0
        bg-gradient-to-r from-[#8B1E1E] to-[#D83B29]
        -translate-x-full
        group-hover:translate-x-0
        transition-transform duration-500 ease-out
      "
                            ></span>

                            {/* Text */}
                            <span className="relative z-10 group-hover:text-white transition-all duration-300">
                                View Properties
                            </span>

                            {/* Icon */}
                            <span className="relative z-10 group-hover:text-white transition-all duration-300">
                                <HiEye className="w-5 h-5" />
                            </span>
                        </button>
                    </div>


                    {/* Properties */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-8 py-6 w-full">
                        {allProperties.map((p) => (
                            <article
                                key={p.id}
                                className="bg-white dark:bg-slate-900 rounded-2xl shadow-lg overflow-hidden 
            transition-all duration-300 hover:shadow-2xl hover:-translate-y-2"
                            >

                                {/* Image — fixed aspect ratio + cover avoids stretched gate photos */}
                                <div className="relative w-full aspect-[4/3] group overflow-hidden bg-slate-100 dark:bg-slate-800">
                                    <img
                                        src={p.image}
                                        alt={p.title}
                                        className="absolute inset-0 h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                                    />

                                    {/* Glass Tag */}
                                    <div className="absolute top-4 left-4 px-3 py-1 rounded-full backdrop-blur-md bg-white/40 dark:bg-slate-900/70 
                    text-[#3B2B9A] dark:text-indigo-200 font-semibold text-sm shadow-sm">
                                        {p.tag}
                                    </div>

                                    {/* View Button */}
                                    <button type="button" className="absolute bottom-4 right-4 p-2 bg-white/90 dark:bg-slate-800 rounded-full shadow-lg
                    hover:bg-white dark:hover:bg-slate-700 transition">
                                        <FaEye size={18} className="text-[#3B2B9A] dark:text-indigo-300" />
                                    </button>
                                </div>

                                {/* Content */}
                                <div className="p-5">
                                    <h3 className="font-semibold text-lg text-slate-800 dark:text-slate-100">
                                        {p.title}
                                    </h3>

                                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                                        {p.location}
                                    </p>

                                    <p className="text-xs text-gray-500 dark:text-slate-400">
                                        {p.subLocation}
                                    </p>

                                    <div className="mt-4 flex items-center justify-between">
                                        <div className="text-[#D83B29] font-bold text-lg">
                                            {p.priceDisplay.split("/")[0]?.trim()}
                                            <span className="text-xs text-gray-500 dark:text-slate-400 font-normal"> / sq yd</span>
                                        </div>
                                    </div>

                                    <button
                                        onClick={() => setContactOpen(true)}
                                        className="
                        mt-4 w-full py-2 rounded-xl relative overflow-hidden
                        bg-gradient-to-r from-[#3B2B9A] to-[#5A47D8]
                        border border-white/30 
                        text-white font-medium shadow-lg
                        transition-all duration-300
                        group
                    "
                                    >
                                        Enquire Now
                                        <span
                                            className="
                            absolute inset-0 bg-white/20
                            -translate-x-[120%] 
                            group-hover:translate-x-[120%]
                            transition-all duration-700 ease-in-out
                            skew-x-[45deg]
                        "
                                        ></span>
                                    </button>
                                </div>
                            </article>
                        ))}
                    </div>

                </div>
            </section>

            {/* WHAT ARE YOU LOOKING FOR */}
            <section id="services" className="mx-auto py-16 text-center bg-white dark:bg-slate-950">
                {/* Heading */}
                <h3
                    className="text-3xl md:text-4xl font-bold text-[#3B2B9A] dark:text-indigo-300 relative inline-block"
                    data-aos="fade-up"
                >
                    What Are You Looking For?
                    <span className="absolute left-1/2 -bottom-3 -translate-x-1/2 w-20 h-1.5 
          bg-gradient-to-r from-[#3B2B9A] to-[#5A47D8] rounded-full"></span>
                </h3>

                <p
                    className="text-slate-600 dark:text-slate-300 mt-4 max-w-xl mx-auto"
                    data-aos="fade-up"
                    data-aos-delay="100"
                >
                    Choose the property type that matches your vision
                </p>

                {/* Cards */}
                <div className="mx-auto px-6 flex flex-col sm:flex-row justify-center gap-8 mt-14 hover:cursor-pointer">
                    {[
                        {
                            name: "Farmhouse",
                            icon: <FaHome />,
                            desc: "Peaceful living surrounded by nature",
                        },
                        {
                            name: "Plot / Land",
                            icon: <FaMapMarkedAlt />,
                            desc: "Build your dream from the ground up",
                        },
                    ].map((item, i) => (
                        <div
                            key={item.name}
                            data-aos="fade-up"
                            data-aos-delay={i * 150}
                            className="group relative w-full sm:w-[320px] 
        rounded-3xl p-8 text-left
        bg-white/90 dark:bg-slate-800/95 backdrop-blur-xl
        border border-slate-200 dark:border-slate-600
        shadow-lg hover:shadow-2xl
        transition-all duration-500
        hover:-translate-y-3"
                        >
                            {/* Glow */}
                            <div className="absolute inset-0 rounded-3xl 
          bg-gradient-to-br from-[#3B2B9A]/10 to-[#5A47D8]/10 
          opacity-0 group-hover:opacity-100 transition duration-500"></div>

                            {/* Icon */}
                            <div
                                className="relative h-16 w-16 rounded-2xl 
          bg-gradient-to-br from-[#3B2B9A] to-[#5A47D8]
          text-white grid place-items-center text-2xl mb-6
          shadow-md group-hover:scale-110 transition"
                            >
                                {item.icon}
                            </div>

                            {/* Content */}
                            <h4 className="relative text-xl font-bold text-slate-800 dark:text-slate-100 mb-2">
                                {item.name}
                            </h4>

                            <p className="relative text-slate-600 dark:text-slate-300 text-sm mb-5">
                                {item.desc}
                            </p>

                            {/* CTA */}
                            <span onClick={() => (window.location.href = "/properties")} className="relative inline-flex items-center gap-2 
          text-sm font-semibold text-[#3B2B9A] dark:text-indigo-300 group-hover:gap-3 transition-all cursor-pointer">
                                Explore →
                            </span>
                        </div>
                    ))}
                </div>
            </section>

            {/* SERVICES SECTION */}
            <section className="py-16 mx-auto bg-slate-100 dark:bg-slate-800">
                <div className="max-w-7xl mx-auto">
                    {/* Heading */}
                    <div className="text-center mb-16">
                        <h3 className="text-4xl font-bold text-[#3B2B9A] dark:text-indigo-300 mb-4 px-4">
                            Trusted Services for Residential & Commercial Properties
                        </h3>
                        <div className="w-24 h-1 bg-gradient-to-r from-[#3B2B9A] to-[#5A47D8] mx-auto rounded-full"></div>
                    </div>

                    {/* Services Grid */}
                    <div className="mx-auto px-6 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {services.map((service, index) => (
                            <div
                                key={service.id}
                                className="bg-white dark:bg-slate-800 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-slate-100 dark:border-slate-600"
                            >
                                {/* Icon */}
                                <div className="w-16 h-16 bg-gradient-to-br from-[#3B2B9A] to-[#5A47D8] rounded-xl flex items-center justify-center text-white text-2xl mb-6 shadow-lg">
                                    {service.icon}
                                </div>

                                {/* Title */}
                                <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-4">
                                    {service.title}
                                </h3>

                                {/* Description */}
                                <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                                    {service.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* TEAM SECTION */}
            <section className="py-16 mx-auto bg-white dark:bg-slate-950">
                <div className="max-w-6xl mx-auto text-center">
                    {/* Heading */}
                    <div className="mb-12 px-6">
                        <h3 className="text-4xl font-bold text-[#3B2B9A] dark:text-indigo-300 mb-4">
                            Your Deal Closing Dream Team
                        </h3>
                        <div className="w-24 h-1 bg-gradient-to-r from-[#3B2B9A] to-[#5A47D8] mx-auto rounded-full mb-6"></div>
                        <p className="text-lg text-slate-700 dark:text-slate-200 max-w-3xl mx-auto leading-relaxed">
                            We have sharp market instincts and skill to turn complex closings into stress-free wins. From first conversation to signing day, your trusted real estate team is on point, every single step.
                        </p>
                    </div>

                    <div className="mx-auto px-6">
                        {/* Team Visual Element */}
                        <div
                            className="
      bg-gradient-to-br from-[#3B2B9A]/5 to-[#5A47D8]/5
      rounded-2xl sm:rounded-3xl
      p-6 sm:p-8 lg:p-12
      border border-[#3B2B9A]/10
    "
                        >
                            <div
                                className="
        flex flex-col sm:flex-row
        flex-wrap
        justify-center items-center
        gap-6 sm:gap-8
      "
                            >
                                {/* Item 1 */}
                                <div className="text-center">
                                    <div
                                        className="
            w-20 h-20 sm:w-24 sm:h-24
            bg-gradient-to-br from-[#3B2B9A] to-[#5A47D8]
            rounded-full
            flex items-center justify-center
            text-white text-2xl sm:text-3xl
            mx-auto mb-3
            shadow-xl
          "
                                    >
                                        <FaUsers />
                                    </div>
                                    <p className="font-semibold text-slate-900 dark:text-slate-100 text-sm sm:text-base">
                                        Expert Team
                                    </p>
                                </div>

                                {/* Plus */}
                                <div className="text-3xl sm:text-4xl text-[#3B2B9A] dark:text-indigo-300 font-semibold">
                                    +
                                </div>

                                {/* Item 2 */}
                                <div className="text-center">
                                    <div
                                        className="
            w-20 h-20 sm:w-24 sm:h-24
            bg-gradient-to-br from-[#3B2B9A] to-[#5A47D8]
            rounded-full
            flex items-center justify-center
            text-white text-2xl sm:text-3xl
            mx-auto mb-3
            shadow-xl
          "
                                    >
                                        <FaChartLine />
                                    </div>
                                    <p className="font-semibold text-slate-900 dark:text-slate-100 text-sm sm:text-base">
                                        Market Insight
                                    </p>
                                </div>

                                {/* Equals */}
                                <div className="text-3xl sm:text-4xl text-[#3B2B9A] dark:text-indigo-300 font-semibold">
                                    =
                                </div>

                                {/* Item 3 */}
                                <div className="text-center">
                                    <div
                                        className="
            w-20 h-20 sm:w-24 sm:h-24
            bg-gradient-to-br from-[#FFD700] to-[#FFA500]
            rounded-full
            flex items-center justify-center
            text-white text-2xl sm:text-3xl
            mx-auto mb-3
            shadow-xl
          "
                                    >
                                        <FaShieldAlt />
                                    </div>
                                    <p className="font-semibold text-slate-900 dark:text-slate-100 text-sm sm:text-base">
                                        Stress-Free Wins
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </section>

            {/* WHY CHOOSE US SECTION */}
            <section className="py-16 mx-auto bg-slate-50 dark:bg-slate-950">
                <div className="max-w-7xl mx-auto">
                    {/* Heading */}
                    <div className="text-center mb-16 px-6">
                        <h3 className="text-4xl font-bold text-[#3B2B9A] dark:text-indigo-300 mb-4">
                            Why Landmarks Realty Is the Smart Choice for Property Investors
                        </h3>
                        <div className="w-24 h-1 bg-gradient-to-r from-[#3B2B9A] to-[#5A47D8] mx-auto rounded-full mb-6"></div>
                        <p className="text-lg text-slate-700 dark:text-slate-200 max-w-4xl mx-auto leading-relaxed">
                            Landmarks Realty blends sharp market insight with hands-on deal experience to help investors spot solid opportunities and avoid costly missteps. With a focus on numbers and long‑term value, the team treats every purchase like a serious business decision, not as a transaction.
                        </p>
                    </div>

                    {/* Features in One Row */}
                    <div className="mx-auto px-6 grid md:grid-cols-3 gap-8">
                        {whyChooseFeatures.map((feature) => (
                            <div
                                key={feature.id}
                                className="bg-white dark:bg-slate-900 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border-t-4 border-[#3B2B9A]"
                            >
                                {/* Icon */}
                                <div className="w-20 h-20 bg-gradient-to-br from-[#3B2B9A] to-[#5A47D8] rounded-full flex items-center justify-center text-white text-3xl mb-6 mx-auto shadow-xl">
                                    {feature.icon}
                                </div>

                                {/* Title */}
                                <h3 className="text-2xl font-bold text-slate-900 dark:text-slate-100 mb-4 text-center">
                                    {feature.title}
                                </h3>

                                {/* Description */}
                                <p className="text-slate-600 dark:text-slate-300 leading-relaxed text-center">
                                    {feature.description}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* TESTIMONIALS */}
            <section className="py-16 bg-slate-100 dark:bg-slate-800">
                <div className="max-w-6xl mx-auto px-6 text-center">
                    {/* Heading */}
                    <h3
                        className="text-3xl font-bold text-[#3B2B9A] dark:text-indigo-300 relative inline-block"
                        data-aos="fade-up"
                    >
                        What Our Homebuyers Are Saying
                        <span className="absolute left-1/2 -bottom-2 -translate-x-1/2 w-16 h-1 
                     bg-gradient-to-r from-[#3B2B9A] to-[#5A47D8] rounded-full"></span>
                    </h3>

                    <p
                        className="text-slate-600 dark:text-slate-300 mt-3 mb-12"
                        data-aos="fade-up"
                        data-aos-delay="80"
                    >
                        Real voices from people who have closed on their dream place with our team that had their back at every step.
                    </p>

                    {/* Slider Container */}
                    <div className="relative mx-auto max-w-6xl">
                        {/* Testimonial Cards */}
                        <div className="relative overflow-hidden px-4 sm:px-8 lg:px-12">
                            <div
                                className="flex transition-transform duration-700 ease-in-out"
                                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                            >
                                {testimonials.map((t) => (
                                    <div key={t.id} className="w-full flex-shrink-0 px-2 sm:px-4">
                                        <blockquote
                                            className="
              relative group
              bg-white dark:bg-slate-900 backdrop-blur-xl border border-slate-200 dark:border-slate-600
              rounded-2xl sm:rounded-3xl
              p-6 sm:p-8 lg:p-10
              shadow-lg sm:shadow-xl
              overflow-hidden
              hover:shadow-2xl
              transition-shadow duration-300
            "
                                        >
                                            {/* Decorative Quote Icon */}
                                            <FaQuoteLeft
                                                className="
                absolute top-4 left-4
                text-[#3B2B9A]/10
                text-4xl sm:text-6xl
                group-hover:text-[#5A47D8]/20
                transform rotate-6
                transition-all duration-500
              "
                                            />

                                            {/* Gradient Accent Line */}
                                            <span className="absolute top-0 left-0 w-1 sm:w-1.5 h-full bg-gradient-to-b from-[#3B2B9A] to-[#5A47D8] rounded-l-3xl"></span>

                                            {/* User Info */}
                                            <div className="flex items-start gap-4 sm:gap-5 relative z-10 mb-5 sm:mb-6">
                                                <img
                                                    src={t.image}
                                                    alt={t.name}
                                                    className="
                  h-14 w-14 sm:h-20 sm:w-20
                  rounded-full shadow-lg
                  object-cover object-center
                  ring-4 ring-[#3B2B9A]/10
                  flex-shrink-0
                "
                                                />

                                                <div className="text-left flex-1">
                                                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-1">
                                                        <strong className="block text-lg sm:text-xl text-slate-900 dark:text-slate-100 font-semibold">
                                                            {t.name}
                                                        </strong>

                                                        {/* Stars */}
                                                        <div className="flex gap-1 text-[#FFD700]">
                                                            {Array.from({ length: 5 }).map((_, s) => (
                                                                <FaStar key={s} className="text-sm sm:text-base" />
                                                            ))}
                                                        </div>
                                                    </div>

                                                    <small className="text-slate-500 dark:text-slate-400 text-xs sm:text-sm block">
                                                        {t.loc}
                                                    </small>
                                                </div>
                                            </div>

                                            {/* Tagline */}
                                            <h4 className="text-[#3B2B9A] dark:text-indigo-300 font-semibold text-base sm:text-lg mb-3 sm:mb-4 relative z-10">
                                                {t.tagline}
                                            </h4>

                                            {/* Testimonial Text */}
                                            <p className="text-slate-700 dark:text-slate-200 leading-relaxed italic relative z-10 text-sm sm:text-base">
                                                "{t.text}"
                                            </p>
                                        </blockquote>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Navigation Buttons */}
                        <button
                            onClick={prevSlide}
                            className="
      absolute left-2 sm:left-0 top-1/2 -translate-y-1/2
      bg-white dark:bg-slate-800 hover:bg-[#3B2B9A]
      text-[#3B2B9A] dark:text-indigo-300 hover:text-white
      p-3 sm:p-4
      rounded-full shadow-lg sm:shadow-xl
      border border-slate-200 dark:border-slate-600
      transition-all duration-300
      hover:scale-110
      active:scale-95
      z-20
    "
                            aria-label="Previous testimonial"
                        >
                            <FaChevronLeft className="text-sm sm:text-lg" />
                        </button>

                        <button
                            onClick={nextSlide}
                            className="
      absolute right-2 sm:right-0 top-1/2 -translate-y-1/2
      bg-white dark:bg-slate-800 hover:bg-[#3B2B9A]
      text-[#3B2B9A] dark:text-indigo-300 hover:text-white
      p-3 sm:p-4
      rounded-full shadow-lg sm:shadow-xl
      border border-slate-200 dark:border-slate-600
      transition-all duration-300
      hover:scale-110
      active:scale-95
      z-20
    "
                            aria-label="Next testimonial"
                        >
                            <FaChevronRight className="text-sm sm:text-lg" />
                        </button>

                        {/* Dots Indicator */}
                        <div className="flex justify-center gap-2 sm:gap-2.5 mt-8 sm:mt-10">
                            {testimonials.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setCurrentSlide(i)}
                                    className={`
          h-2 sm:h-2.5 rounded-full transition-all duration-300
          ${i === currentSlide
                                            ? 'w-8 sm:w-10 bg-[#3B2B9A]'
                                            : 'w-2 sm:w-2.5 bg-slate-300 hover:bg-slate-400'}
        `}
                                    aria-label={`Go to testimonial ${i + 1}`}
                                />
                            ))}
                        </div>
                    </div>

                </div>
            </section>

            {/* FOOTER */}
            <Footer />

            {/* CONTACT MODAL */}
            {contactOpen && (
                <>
                    {/* Backdrop */}
                    <div
                        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 opacity-100 transition-opacity duration-300"
                        onClick={() => {
                            suppressContactAutoOpenRef.current = true;
                            setContactOpen(false);
                        }}
                    ></div>

                    {/* Modal */}
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <form
                            onSubmit={handleContactSubmit}
                            className="lr-callback-panel animate-[fadeInScale_0.35s_ease-out]"
                        >
                            {/* Close Button */}
                            <button
                                type="button"
                                aria-label="Close"
                                onClick={() => {
                                    suppressContactAutoOpenRef.current = true;
                                    setContactOpen(false);
                                }}
                                className="
                                    absolute -top-3 -right-3
                                    bg-white dark:bg-slate-900 backdrop-blur 
                                    text-gray-700 dark:text-gray-300
                                    shadow-md hover:shadow-lg
                                    w-9 h-9 flex items-center justify-center
                                    rounded-full border border-gray-200
                                    transition-all duration-300
                                    hover:scale-110 hover:rotate-90
                                "
                            >
                                ✕
                            </button>


                            {/* Title */}
                            <h3 className="text-2xl font-extrabold text-[#2D1B7E] dark:text-indigo-200 tracking-tight">
                                Request a Callback
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">
                                Share your details and our team will call you within one business day.
                            </p>

                            {/* Inputs */}
                            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                                <input
                                    name="name"
                                    autoComplete="name"
                                    value={callbackForm.name}
                                    onChange={(e) =>
                                        setCallbackForm((prev) => ({
                                            ...prev,
                                            name: e.target.value,
                                        }))
                                    }
                                    placeholder="Full name"
                                    className="lr-callback-field"
                                />

                                <input
                                    type="email"
                                    name="email"
                                    autoComplete="email"
                                    value={callbackForm.email}
                                    onChange={(e) =>
                                        setCallbackForm((prev) => ({
                                            ...prev,
                                            email: e.target.value,
                                        }))
                                    }
                                    placeholder="Email address"
                                    className="lr-callback-field"
                                />

                                <input
                                    type="tel"
                                    name="phone"
                                    inputMode="numeric"
                                    autoComplete="tel"
                                    maxLength={10}
                                    value={callbackForm.phone}
                                    onChange={(e) =>
                                        setCallbackForm((prev) => ({
                                            ...prev,
                                            phone: sanitizeIndianMobileInput(
                                                e.target.value
                                            ),
                                        }))
                                    }
                                    placeholder="Mobile (10 digits, starts 6–9)"
                                    className="lr-callback-field"
                                />

                                <div className="relative" onClick={(e) => e.stopPropagation()} >
                                    <button
                                        type="button"
                                        onClick={() => setInterestOpen(!interestOpen)}
                                        className="lr-callback-field flex items-center justify-between"
                                    >
                                        <span className={interest === "I'm interested in" ? "text-slate-400" : "text-slate-700 dark:text-slate-200"}>
                                            {interest}
                                        </span>

                                        <svg className={`w-4 h-4 transition-transform ${interestOpen ? "rotate-180" : ""}`}
                                            fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>

                                    {interestOpen && (
                                        <div className="absolute left-0 right-0 mt-1 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 border border-slate-300 dark:border-slate-600 rounded-lg shadow-xl z-50 overflow-hidden animate-fadeIn">
                                            {["Buying", "Selling", "Investment"].map((item) => (
                                                <div
                                                    key={item}
                                                    className="px-4 py-2 hover:bg-[#D83B29]/10 cursor-pointer text-slate-700 dark:text-slate-200"
                                                    onClick={() => {
                                                        setInterest(item);
                                                        setInterestOpen(false);
                                                    }}
                                                >
                                                    {item}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                </div>
                            </div>

                            <textarea
                                name="message"
                                rows="4"
                                value={callbackForm.message}
                                onChange={(e) =>
                                    setCallbackForm((prev) => ({
                                        ...prev,
                                        message: e.target.value,
                                    }))
                                }
                                placeholder="Message (optional)"
                                className="lr-callback-field resize-none"
                            ></textarea>

                            {/* Footer */}
                            <div className="mt-6 flex items-center justify-between">
                                <button
                                    type="submit"
                                    disabled={
                                        !callbackFormValid || enquirySubmitting
                                    }
                                    className="
        relative overflow-hidden group
        px-6 py-3 rounded-xl text-white font-semibold
        bg-gradient-to-r from-[#8B1E1E] to-[#D83B29]
        shadow-lg hover:shadow-xl
        hover:brightness-105 transition-all duration-300
        disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:brightness-100
    "
                                >
                                    {enquirySubmitting
                                        ? "Sending..."
                                        : "Submit Request"}

                                    <span
                                        className="
            absolute inset-0 bg-white/20
            translate-x-[-120%]
            group-hover:translate-x-[120%]
            transition-all duration-700 ease-in-out
            skew-x-[45deg]
        "
                                    ></span>
                                </button>


                                <small className="text-gray-600 dark:text-gray-400">We respect your privacy.</small>
                            </div>
                        </form>
                    </div>

                    {/* Animation Keyframe */}
                    <style>
                        {`
                @keyframes fadeInScale {
                    0% { opacity: 0; transform: translateY(20px) scale(0.95); }
                    100% { opacity: 1; transform: translateY(0) scale(1); }
                }
            `}
                    </style>
                </>
            )}

        </div>
    );
}
